import { create } from "zustand";

export type EnterHardPathOptions = {
  /** Show unsaved-loss warning (chunk-fail, or Reload now while dirty). */
  unsavedLoss?: boolean;
  /**
   * Skip automatic reload and leave the fallback Reload control usable
   * (e.g. offline — BR-11 / E-09).
   */
  skipAutoReload?: boolean;
};

/**
 * Per-tab app update prompt orchestration (ephemeral; no cross-tab channel).
 * Soft UI waits for soft triggers after update known (BR-15).
 */
interface AppUpdatePromptState {
  /** Newer worker/bundle is available for this tab (FR-01). */
  updateKnown: boolean;
  /** Soft banner currently visible. */
  softBannerVisible: boolean;
  /** Blocking hard-path dialog active; soft banner suppressed. */
  hardActive: boolean;
  /** Unsaved-loss line on hard dialog. */
  showUnsavedLossWarning: boolean;
  /** Auto-reload failed or was skipped; show fallback Reload. */
  autoReloadFailed: boolean;
  markUpdateKnown: () => void;
  /** Route change or meaningful click (FR-04, BR-01, BR-03). */
  notifySoftTrigger: () => void;
  /** Later — hide until next soft trigger (BR-02, BR-03). */
  dismissSoftLater: () => void;
  /** Soft Reload now → hard path (BR-04). */
  requestReloadNow: (options?: { unsavedLoss?: boolean }) => void;
  enterHardPath: (options?: EnterHardPathOptions) => void;
  markAutoReloadFailed: () => void;
  /** Manual fallback Reload on hard dialog (BR-10). */
  retryReload: () => void;
}

type PerformReloadFn = () => void;

let performReload: PerformReloadFn = () => {
  window.location.reload();
};

let autoReloadTimer: number | undefined;
let autoReloadFailTimer: number | undefined;

export function setAppUpdatePerformReload(fn: PerformReloadFn): void {
  performReload = fn;
}

export function resetAppUpdatePerformReload(): void {
  performReload = () => {
    window.location.reload();
  };
}

function clearReloadTimers(): void {
  if (autoReloadTimer !== undefined) {
    window.clearTimeout(autoReloadTimer);
    autoReloadTimer = undefined;
  }
  if (autoReloadFailTimer !== undefined) {
    window.clearTimeout(autoReloadFailTimer);
    autoReloadFailTimer = undefined;
  }
}

function invokeReloadNow(): void {
  clearReloadTimers();
  try {
    performReload();
    // If the document is still here shortly after, surface fallback Reload (E-09).
    autoReloadFailTimer = window.setTimeout(() => {
      useAppUpdatePromptStore.getState().markAutoReloadFailed();
    }, 2_500);
  } catch {
    useAppUpdatePromptStore.getState().markAutoReloadFailed();
  }
}

function scheduleAutoReload(): void {
  clearReloadTimers();
  // Let the hard dialog paint before navigating away (AC-03).
  autoReloadTimer = window.setTimeout(() => {
    invokeReloadNow();
  }, 150);
}

export const useAppUpdatePromptStore = create<AppUpdatePromptState>((set, get) => ({
  updateKnown: false,
  softBannerVisible: false,
  hardActive: false,
  showUnsavedLossWarning: false,
  autoReloadFailed: false,

  markUpdateKnown: () => {
    set({ updateKnown: true });
  },

  notifySoftTrigger: () => {
    const { updateKnown, hardActive } = get();
    if (!updateKnown || hardActive) {
      return;
    }
    // BR-01 / BR-03 / BR-15: show (or re-show after Later) on soft trigger only.
    set({ softBannerVisible: true });
  },

  dismissSoftLater: () => {
    // BR-02 / BR-03: hide until the next soft trigger; not a permanent mute.
    set({ softBannerVisible: false });
  },

  requestReloadNow: (options) => {
    get().enterHardPath({ unsavedLoss: options?.unsavedLoss });
  },

  enterHardPath: (options) => {
    if (get().hardActive) {
      if (options?.unsavedLoss) {
        set({ showUnsavedLossWarning: true });
      }
      return;
    }

    clearReloadTimers();
    set({
      updateKnown: true,
      hardActive: true,
      softBannerVisible: false,
      showUnsavedLossWarning: Boolean(options?.unsavedLoss),
      autoReloadFailed: false,
    });

    if (options?.skipAutoReload) {
      set({ autoReloadFailed: true });
      return;
    }

    // BR-10 / E-09: always attempt a full document reload, including while
    // navigator.onLine is false (SW cache can still serve; Reload stays if it fails).
    scheduleAutoReload();
  },

  markAutoReloadFailed: () => {
    if (!get().hardActive) {
      return;
    }
    set({ autoReloadFailed: true });
  },

  retryReload: () => {
    if (!get().hardActive) {
      return;
    }
    set({ autoReloadFailed: false });
    // Immediate reload keeps the click as a user gesture (E-16 / AC-11).
    invokeReloadNow();
  },
}));

/** Test helper — reset ephemeral prompt state between cases. */
export function resetAppUpdatePromptStore(): void {
  clearReloadTimers();
  useAppUpdatePromptStore.setState({
    updateKnown: false,
    softBannerVisible: false,
    hardActive: false,
    showUnsavedLossWarning: false,
    autoReloadFailed: false,
  });
}
